import { useCallback, useEffect, useRef, useState } from 'react';
import { withBasePath } from '@src/common/path-convert';
import { sortContentBlocksByScores } from '@src/common/scores/sort-contents';
import { useRouter } from 'next/router';
import {
    blockTypeLinkTo,
    isDeepLinkShareUrl,
    isShareUrl,
    isTimeFrame,
    isVideoPop,
    objFilter,
    removeCIDUrl,
    videoNative,
} from '@src/utils/util';
import { jsBridge } from '@src/utils/js-bridge';
import SectionSorting from '@components/section-sorting/SectionSorting';
import { useGlobalStore } from '@src/hooks/use-global-store';
import { observer } from 'mobx-react-lite';
import { TagContentBlockFragment } from '@src/common/types';
import isSharePageStore from '@mobx/stores/isShare-store';
import cn from 'classnames';
import Open3in1App from '@components/open-3in1-app/Open3in1App';

interface Props {
    hashTagsList: TagContentBlockFragment[];
    tagTitleName: string;
}

/** hashTags 卡片样式 */
const hashTagList = observer(({ hashTagsList, tagTitleName }: Props) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const router = useRouter();
    const isSharePage = isSharePageStore.isSharePage;
    const { settingStore, videoPopupStore, shareDataStore, baseStore } = useGlobalStore();

    const [sectionList, setSectionList] = useState([] as TagContentBlockFragment[]);
    const [sectionHeight, setSectionHeight] = useState(0);

    const dataSorting = useCallback(
        (key: string) => {
            if (!key) return;
            const a = new Array(...(hashTagsList || []));
            if (key === 'Newest') {
                const newData = a.sort(
                    (a, b) =>
                        (b?.startDatetime?.dateValue as number) -
                        (a?.startDatetime?.dateValue as number)
                );
                setSectionList(newData);
            } else if (key === 'Relevance') {
                const s = sortContentBlocksByScores(
                    a,
                    shareDataStore?.categoryScores,
                    shareDataStore?.hashtagScores,
                    baseStore?.data?.contentScoringConfig || {}
                );
                setSectionList(s);
            } else {
                setSectionList(hashTagsList || []);
            }
        },
        [
            baseStore?.data?.contentScoringConfig,
            hashTagsList,
            shareDataStore?.categoryScores,
            shareDataStore?.hashtagScores,
        ]
    );

    useEffect(() => {
        const hasTagSorting = sessionStorage.getItem('hasTagSortingKey');
        if (hasTagSorting) {
            dataSorting(hasTagSorting);
        } else {
            dataSorting('Relevance');
        }
    }, [dataSorting]);

    useEffect(() => {
        setSectionHeight(sectionRef?.current ? sectionRef?.current?.offsetHeight : 0);
    }, [sectionList]);

    // console.log('**************************', sectionRef?.current?.offsetHeight);
    return (
        <div className={cn('px-[17px] pt-[54px]', isSharePage && 'max-w-[1440px]')}>
            <div
                ref={sectionRef}
                className={cn(
                    'fixed z-20 bg-[#f6f7f8] flex justify-between w-full pr-[36px]',
                    isSharePage && 'max-w-[1423px]'
                )}
            >
                <div
                    className='text-[18px] pt-[12px] pb-[12px]'
                    dangerouslySetInnerHTML={{
                        __html: tagTitleName || '',
                    }}
                ></div>
                <div>
                    <SectionSorting
                        submitSorting={(key) => {
                            dataSorting(key);
                        }}
                    />
                </div>
            </div>

            <div
                // className='overflow-y-auto'
                style={{ paddingTop: `${sectionHeight + 10}px`, paddingBottom: '20px' }}
                ref={contentRef}
            >
                <div className='md:grid md:gap-4 md:grid-cols-2'>
                    {sectionList.map((i: TagContentBlockFragment, index: number) => {
                        const isDeepLink = i.blockType?.value === 'DeepLink';
                        return (
                            <div key={index} className={cn(isSharePage && 'pointer-events-none')}>
                                {isDeepLink ? (
                                    <div className='relative'>
                                        {i.shareIcon?.boolValue && (
                                            <img
                                                className='w-[20px] h-[22px] z-10 absolute top-[10px] right-[10px]'
                                                src={withBasePath('/images/share_blue.svg')}
                                                alt=''
                                                loading='lazy'
                                                onClick={(e) => {
                                                    e.stopPropagation();

                                                    if (isDeepLink) {
                                                        return jsBridge.appShareUrl(
                                                            isDeepLinkShareUrl(
                                                                i.deepLink?.value ?? ''
                                                            ),
                                                            i
                                                        );
                                                    }
                                                    const shareLink =
                                                        i.blockType?.value === 'Internal'
                                                            ? i.internalLink?.value ?? ''
                                                            : `${i.currentPath}/${i.key}/`;

                                                    jsBridge.appShareUrl(
                                                        `${isShareUrl(
                                                            i.blockType?.value,
                                                            shareLink,
                                                            i.raceLink?.targetItem?.path
                                                        )}`,
                                                        i
                                                    );
                                                }}
                                            />
                                        )}

                                        <a href={i.deepLink?.value ?? ''} target='_blank'>
                                            <div
                                                className={`h-[120px] rounded-[4px] bg-[#fff] p-[10px] relative flex mb-[10px] md:mb-0`}
                                            >
                                                <>
                                                    {isTimeFrame(
                                                        (i.isIconDisplay &&
                                                            i.isIconDisplay?.boolValue) ||
                                                            false,
                                                        i.iconDisplayStartDate?.dateValue || 0,
                                                        i.iconDisplayEndDate?.dateValue || 0
                                                    ) && (
                                                        <img
                                                            src={
                                                                i.iconDisplay?.targetItem?.iconImage
                                                                    ?.src
                                                            }
                                                            alt=''
                                                            loading='lazy'
                                                            className='h-[20px] absolute top-[15px] left-[15px]'
                                                        ></img>
                                                    )}
                                                    <img
                                                        className='w-[100px] h-[100px] rounded-[4px] object-cover'
                                                        src={i?.heroImageSq?.src}
                                                        alt=''
                                                        loading='lazy'
                                                    />
                                                </>
                                                <div className='flex flex-col flex-[1] justify-start pl-[12px] pr-[12px] break-all'>
                                                    <div
                                                        className='text-[13px] mb-[2px]  text-[#031E6C] categoryBr line-clamp-1'
                                                        dangerouslySetInnerHTML={{
                                                            __html: `${
                                                                (i?.category?.categories &&
                                                                    i.category?.categories.length >
                                                                        0 &&
                                                                    objFilter(
                                                                        i?.category?.categories
                                                                    )[0]?.categoryTitle?.value) ||
                                                                ''
                                                            }`,
                                                        }}
                                                    ></div>
                                                    <div
                                                        className='text-[15px] line-clamp-4 align-bottom leading-[1.27] min-h-[78px]'
                                                        style={{ wordBreak: 'break-word' }}
                                                        dangerouslySetInnerHTML={{
                                                            __html: i.pageTitle?.value || '',
                                                        }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </a>
                                    </div>
                                ) : (
                                    <div
                                        className={`h-[120px] rounded-[4px] bg-[#fff] p-[10px] relative flex mb-[10px] md:mb-0 ${isVideoPop(
                                            i
                                        )}`}
                                        data-poster={i.videoLink?.value}
                                        data-image={i.videoPoster?.src}
                                        onClick={() => {
                                            videoNative(i, jsBridge);

                                            if (i.blockType?.value === 'Video') {
                                                return videoPopupStore.updateContentBlockData(i);
                                            }

                                            const generalLink = removeCIDUrl(
                                                `/${i.currentPath}/${i.key}${location.search}`
                                            );

                                            blockTypeLinkTo(
                                                i,
                                                generalLink,
                                                router,
                                                jsBridge,
                                                settingStore
                                            );
                                        }}
                                    >
                                        <>
                                            {isTimeFrame(
                                                (i.isIconDisplay && i.isIconDisplay?.boolValue) ||
                                                    false,
                                                i.iconDisplayStartDate?.dateValue || 0,
                                                i.iconDisplayEndDate?.dateValue || 0
                                            ) && (
                                                <img
                                                    src={i.iconDisplay?.targetItem?.iconImage?.src}
                                                    alt=''
                                                    loading='lazy'
                                                    className='h-[20px] absolute top-[15px] left-[15px]'
                                                ></img>
                                            )}
                                            <img
                                                className='w-[100px] h-[100px] rounded-[4px] object-cover'
                                                src={i?.heroImageSq?.src}
                                                alt=''
                                                loading='lazy'
                                            />
                                        </>
                                        <div className='flex flex-col flex-[1] justify-start pl-[12px] break-all'>
                                            <div
                                                className='text-[13px] mb-[2px]  text-[#031E6C] categoryBr line-clamp-1'
                                                dangerouslySetInnerHTML={{
                                                    __html: `${
                                                        (i?.category?.categories &&
                                                            i.category?.categories.length > 0 &&
                                                            objFilter(i?.category?.categories)[0]
                                                                ?.categoryTitle?.value) ||
                                                        ''
                                                    }`,
                                                }}
                                            ></div>
                                            <div
                                                className='text-[15px] line-clamp-4 align-bottom leading-[1.27] min-h-[78px]'
                                                style={{ wordBreak: 'break-word' }}
                                                dangerouslySetInnerHTML={{
                                                    __html: i.pageTitle?.value || '',
                                                }}
                                            ></div>
                                        </div>
                                        {i.shareIcon?.boolValue && (
                                            <img
                                                className='w-[20px] h-[22px] z-10'
                                                src={withBasePath('/images/share_blue.svg')}
                                                alt=''
                                                loading='lazy'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const shareLink =
                                                        i.blockType?.value === 'Internal'
                                                            ? i.internalLink?.value ?? ''
                                                            : `${i.currentPath}/${i.key}/`;

                                                    jsBridge.appShareUrl(
                                                        `${isShareUrl(
                                                            i.blockType?.value,
                                                            shareLink,
                                                            i.raceLink?.targetItem?.path
                                                        )}`,
                                                        i
                                                    );
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
            {isSharePage && (
                <Open3in1App
                    currentPath={''}
                    getOverLayHeight={(v) => console.log('hash-H--', v)}
                />
            )}
        </div>
    );
});

export default hashTagList;
